import React, { useEffect } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { Skeleton } from 'antd';
import './JobsDetailsSidebar.scss';
import {
  JobDetailsSidebarProps,
  STATUS_CONFIG,
  Section,
  formatDate,
  Row,
  formatDuration,
  LANG_STYLES,
  CopyableValue,
  formatMemory,
} from './JobDetailsSidebarHelpers';
import JobPropertiesModal from './Modals/JobPropertiesModal';
import JobCodeModal from './Modals/JobCodeModal';
import { useGetJobDetail } from 'hooks/jobs';
import HoverActions from 'components/JobsTable/HoverActions/HoverActions';
import useSignedURL from 'hooks/useSignedURL';
import { JobStepState, JobStep, JobState } from 'types/types';

export function JobDetailsSidebar({
  jobId,
  clusterName,
  onClose,
}: JobDetailsSidebarProps) {
  const isOpen = Boolean(jobId);
  const {
    isSignedURL,
    expires,
    signature,
    prefix,
    schemaFromSignedURL: schemaName,
  } = useSignedURL();
  const { data: jobDetails, isLoading: isFetching } = useGetJobDetail({
    schemaName: schemaName ?? '',
    jobId: jobId ?? '',
    isEnabled: !!jobId,
    isSignedURL,
    expires: expires ? expires : undefined,
    signature: signature ? signature : undefined,
    prefix: prefix ? prefix : undefined,
    clusterName: clusterName ?? '',
  });
  const [jobPropOpened, setJobPropOpened] = React.useState(false);
  const [jobCodeOpened, setJobCodeOpened] = React.useState(false);

  const statusCfg = jobDetails
    ? STATUS_CONFIG[jobDetails.sparkJobStatus as unknown as JobStatus]
    : null;
  const isDownloadLogsBtnDisabled =
    jobDetails?.sparkJobStatus === JobStatus.In_Queue;

  function onCloseModal() {
    onClose();
    setJobPropOpened(false);
    setJobCodeOpened(false);
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseModal();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Blur overlay */}
      <div
        aria-hidden="true"
        onClick={onCloseModal}
        className={[
          'job-details-sidebar__overlay',
          isOpen && 'job-details-sidebar__overlay--open',
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {/* Sidebar panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Job details"
        className={[
          'job-details-sidebar__panel',
          isOpen && 'job-details-sidebar__panel--open',
        ]
          .filter(Boolean)
          .join(' ')}>
        {/* Header */}
        <div className="job-details-sidebar__header">
          <div className="job-details-sidebar__header-left">
            <span className="job-details-sidebar__eyebrow">Job Details: </span>
            {isFetching ? (
              <Skeleton.Input active size="small" style={{ width: 260 }} />
            ) : (
              <h2 className="job-details-sidebar__title">
                {jobDetails?.sparkJobId ?? '—'}
              </h2>
            )}
          </div>
          <div className="job-details-sidebar__header-actions">
            <button
              onClick={onCloseModal}
              className="job-details-sidebar__close-btn"
              aria-label="Close">
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Status row */}
        {!isFetching && statusCfg && jobDetails && (
          <div className="job-details-sidebar__status-row">
            <span
              className="job-details-sidebar__status-pill"
              style={{ color: statusCfg.color, background: statusCfg.bg }}>
              {statusCfg.icon}
              <span style={{ marginLeft: 6 }}>{statusCfg.label}</span>
            </span>
          </div>
        )}

        {/* Scrollable content */}
        <div className="job-details-sidebar__content">
          {isFetching ? (
            <div className="job-details-sidebar__skeleton">
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          ) : jobDetails ? (
            <>
              <Section title="Identity">
                <Row label="Job ID">
                  <CopyableValue value={jobDetails.sparkJobId} />
                </Row>
                <Row label="App ID">
                  <CopyableValue value={jobDetails.sparkAppId} />
                </Row>
                <Row label="Table">
                  <span className="job-details-sidebar__mono">
                    {jobDetails.schemaName}.{jobDetails.tableName}
                  </span>
                </Row>
                <Row label="Schema">
                  <span className="job-details-sidebar__mono">
                    {jobDetails.schemaName}
                  </span>
                </Row>
                <Row label="Cluster">
                  <span className="job-details-sidebar__mono">
                    {jobDetails.clusterName}
                  </span>
                </Row>
                <Row label="Cluster Prefix - Cluster URL">
                  <span className="job-details-sidebar__mono">
                    {jobDetails.clusterPrefix} / {jobDetails.clusterUrl}
                  </span>
                </Row>
                <Row label="Vendor">{jobDetails.vendor}</Row>
              </Section>

              <div className="job-details-sidebar__divider" />

              <Section title="Timing">
                <Row label="Created">{formatDate(jobDetails.createdAt)}</Row>
                <Row label="Updated">
                  {formatDate(jobDetails?.updatedAt ?? null)}
                </Row>
                <Row label="Dequeued">
                  {formatDate(jobDetails?.dequeuedAt ?? null)}
                </Row>
                <Row label="Duration">
                  <span className="job-details-sidebar__duration">
                    {formatDuration(jobDetails.duration)}
                  </span>
                </Row>
              </Section>

              <div className="job-details-sidebar__divider" />

              <Section title="Resources">
                <Row label="Language">
                  <span
                    className="job-details-sidebar__lang-pill"
                    style={LANG_STYLES[jobDetails.mvLanguage] || {}}>
                    {jobDetails?.mvLanguage ?? '—'}
                  </span>
                </Row>
                <Row label="Executors">{jobDetails.totalExecutorCount}</Row>
                <Row label="Peak memory">
                  {formatMemory(jobDetails.peakMemoryInMB)}
                </Row>
                <Row label="Driver memory">
                  {formatMemory(jobDetails.sparkDriverMemoryInMB)}
                </Row>
              </Section>

              <div className="job-details-sidebar__divider" />

              <Section title="ICDB data">
                <Row label="Job State">
                  {JobState[Number(jobDetails.jobState)]}
                </Row>
                <Row label="Extracted Row Count">
                  {jobDetails.extractedRowCount?.toLocaleString() ?? '—'}
                </Row>
                <Row label="Rejected Row Count">
                  {jobDetails.rejectedRowCount?.toLocaleString() ?? '—'}
                </Row>
                <Row label="Loaded Row Count">
                  {jobDetails.loadedRowCount?.toLocaleString() ?? '—'}
                </Row>
                {jobDetails.jobStates && jobDetails.jobStates.length > 0 && (
                  <Row label="Job States:">
                    <div className="job-details-sidebar__job-states">
                      {jobDetails.jobStates.map(
                        (state: { stateId: number; stateResult: string }) => (
                          <div
                            key={state.stateId}
                            className="job-details-sidebar__job-state-item">
                            <span className="job-details-sidebar__mono">
                              {JobStep[state.stateId]} :{' '}
                              {JobStepState[Number(state.stateResult)]}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </Row>
                )}
              </Section>

              <div className="job-details-sidebar__divider" />
              <Section title="Actions">
                <HoverActions
                  tableName={jobDetails?.tableName ?? ''}
                  sparkJobID={jobDetails?.sparkJobId ?? '-'}
                  isDownloadLogsBtnDisabled={isDownloadLogsBtnDisabled}
                  clusterUrl={jobDetails?.clusterUrl ?? ''}
                  sparkJobStatus={jobDetails?.sparkJobStatus ?? ''}
                  sparkAppID={jobDetails?.sparkAppId ?? ''}
                  clusterName={jobDetails?.clusterName ?? ''}
                  schemaName={jobDetails?.schemaName ?? ''}
                  clusterPrefix={jobDetails?.clusterPrefix ?? ''}
                  setPropertiesOpen={setJobPropOpened}
                  setCodeOpen={setJobCodeOpened}
                  jobPropOpened={jobPropOpened}
                  jobCodeOpened={jobCodeOpened}
                />
              </Section>
            </>
          ) : (
            <div className="job-details-sidebar__empty">
              No job data available.
            </div>
          )}
        </div>
      </aside>
      <JobPropertiesModal
        job={jobDetails}
        onClose={() => setJobPropOpened(false)}
        isLoading={isFetching}
        clusterName={jobDetails?.clusterName ?? ''}
        isOpen={jobPropOpened}
      />
      <JobCodeModal
        job={jobDetails}
        onClose={() => setJobCodeOpened(false)}
        isLoading={isFetching}
        clusterName={jobDetails?.clusterName ?? ''}
        isOpen={jobCodeOpened}
      />
    </>
  );
}

export default JobDetailsSidebar;
